import {
  Check,
  Search,
  X,
  type LucideIcon,
} from "lucide-react";
import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type RefObject,
} from "react";

export interface CadenceCommandPaletteAction {
  id: string;
  label: string;
  detail: string;
  icon: LucideIcon;
  disabled?: boolean;
  run(): void;
}

interface CadenceCommandPaletteProps {
  actions: CadenceCommandPaletteAction[];
  enabled: boolean;
  open: boolean;
  triggerRef: RefObject<HTMLButtonElement>;
  onOpenChange(open: boolean): void;
}

function focusableIn(element: HTMLElement) {
  return Array.from(
    element.querySelectorAll<HTMLElement>(
      'button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])',
    ),
  );
}

/**
 * A local index of visible workspace actions. It deliberately holds no route
 * or Runtime state: each command delegates to the same callback as its
 * visible control, preserving Project permissions and side-effect boundaries.
 */
export function CadenceCommandPalette({
  actions,
  enabled,
  open,
  triggerRef,
  onOpenChange,
}: CadenceCommandPaletteProps) {
  const panelRef = useRef<HTMLElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const previousFocusRef = useRef<HTMLElement>();
  const [query, setQuery] = useState("");
  const [activeIndex, setActiveIndex] = useState(0);
  const filteredActions = useMemo(() => {
    const terms = query.trim().toLocaleLowerCase();
    if (!terms) return actions;
    return actions.filter((action) =>
      `${action.label} ${action.detail}`.toLocaleLowerCase().includes(terms),
    );
  }, [actions, query]);

  const close = (restoreFocus = true) => {
    onOpenChange(false);
    if (!restoreFocus) return;
    window.setTimeout(() => {
      const target = previousFocusRef.current ?? triggerRef.current;
      if (target?.isConnected && !target.hasAttribute("disabled")) {
        target.focus();
      }
    }, 0);
  };

  const openPalette = () => {
    if (!enabled) return;
    previousFocusRef.current =
      document.activeElement instanceof HTMLElement
        ? document.activeElement
        : undefined;
    onOpenChange(true);
  };

  const runAction = (action: CadenceCommandPaletteAction) => {
    if (action.disabled) return;
    close(false);
    action.run();
  };

  useEffect(() => {
    if (enabled || !open) return;
    onOpenChange(false);
  }, [enabled, onOpenChange, open]);

  useEffect(() => {
    if (!open) return;
    setQuery("");
    setActiveIndex(0);
    const focusFrame = window.requestAnimationFrame(() => inputRef.current?.focus());
    return () => window.cancelAnimationFrame(focusFrame);
  }, [open]);

  useEffect(() => {
    setActiveIndex((current) =>
      Math.min(Math.max(current, 0), Math.max(0, filteredActions.length - 1)),
    );
  }, [filteredActions.length]);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (
        enabled &&
        !event.isComposing &&
        !event.altKey &&
        (event.metaKey || event.ctrlKey) &&
        event.key.toLocaleLowerCase() === "k"
      ) {
        event.preventDefault();
        if (open) close();
        else openPalette();
        return;
      }
      if (!open) return;
      if (event.key === "Escape") {
        event.preventDefault();
        close();
        return;
      }
      if (event.key === "ArrowDown" || event.key === "ArrowUp") {
        if (!filteredActions.length) return;
        event.preventDefault();
        setActiveIndex((current) =>
          event.key === "ArrowDown"
            ? (current + 1) % filteredActions.length
            : (current - 1 + filteredActions.length) % filteredActions.length,
        );
        return;
      }
      if (event.key === "Enter") {
        const action = filteredActions[activeIndex];
        if (!action) return;
        event.preventDefault();
        runAction(action);
        return;
      }
      if (event.key !== "Tab") return;
      const panel = panelRef.current;
      if (!panel) return;
      const focusable = focusableIn(panel);
      if (!focusable.length) return;
      const currentIndex = focusable.indexOf(document.activeElement as HTMLElement);
      const nextIndex = event.shiftKey
        ? currentIndex <= 0
          ? focusable.length - 1
          : currentIndex - 1
        : currentIndex === focusable.length - 1
          ? 0
          : currentIndex + 1;
      event.preventDefault();
      focusable[nextIndex].focus();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [activeIndex, enabled, filteredActions, open]);

  if (!open) return null;

  return (
    <div
      className="cadence-command-palette-backdrop"
      data-testid="command-palette-backdrop"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) close();
      }}
    >
      <section
        className="cadence-command-palette"
        data-testid="command-palette"
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="cadence-command-palette-title"
      >
        <div className="cadence-command-palette-search">
          <Search size={17} aria-hidden="true" />
          <label className="visually-hidden" htmlFor="cadence-command-query">
            搜索快捷操作
          </label>
          <input
            autoComplete="off"
            id="cadence-command-query"
            placeholder="搜索快捷操作…"
            ref={inputRef}
            type="text"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
          />
          <kbd>Esc</kbd>
          <button
            aria-label="关闭快捷操作"
            className="cadence-command-palette-close"
            type="button"
            onClick={() => close()}
          >
            <X size={16} aria-hidden="true" />
          </button>
        </div>
        <div className="cadence-command-palette-heading">
          <h2 id="cadence-command-palette-title">快捷操作</h2>
          <span>{filteredActions.length ? "当前工作区" : "没有匹配的操作"}</span>
        </div>
        <div className="cadence-command-palette-list" role="listbox">
          {filteredActions.map((action, index) => {
            const Icon = action.icon;
            return (
              <button
                aria-selected={index === activeIndex}
                className="cadence-command-palette-item"
                disabled={action.disabled}
                key={action.id}
                role="option"
                type="button"
                onFocus={() => setActiveIndex(index)}
                onMouseMove={() => setActiveIndex(index)}
                onClick={() => runAction(action)}
              >
                <span className="cadence-command-palette-icon" aria-hidden="true">
                  <Icon size={16} />
                </span>
                <span className="cadence-command-palette-copy">
                  <strong>{action.label}</strong>
                  <small>{action.detail}</small>
                </span>
                {index === activeIndex && <Check size={15} aria-hidden="true" />}
              </button>
            );
          })}
        </div>
        <footer className="cadence-command-palette-footer">
          <span>⌘ / Ctrl + K 打开或关闭</span>
          <span>↑ ↓ 选择 · Enter 执行</span>
        </footer>
      </section>
    </div>
  );
}
